// What the open session asks, docked between its transcript and its
// composer, at the composer's measure: one question at a time, in the one
// question card (questions/QuestionCard.tsx), with a pager when several
// wait, oldest first; each card stays as he left it while he pages. Its
// head says "Asks you" for a question put to the person, "Urgent" and "The
// agent waits" when they hold (questions/waiting.tsx), when it was asked
// when the record says, when it was last revised, and holds the pager and
// the chevron that minimizes it. Urgent questions come first, then those
// their agent still waits on, then the oldest.
//
// Minimized, it is one line: "Asks you", the question in one line, its
// flags, when it was asked, and Answer, which opens it again. It takes its own room, so it
// pushes the transcript up rather than covering it. Whether a session's dock
// is minimized is remembered for the page's life, as he left it.
//
// A question whose expiry has passed is not docked: the transcript shows it
// where it was asked (questions/expired.ts). While one is open, the dock
// keeps one line, "1 expired in the transcript", whose Show goes to it.
import { useEffect, useId, useRef, useState } from 'react'
import type { Question } from '../api/types'
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '../components/Icons'
import { longAge, Time } from '../components/Time'
import { askedAt, askTitle, putTo, useQuestionEntries } from '../questions/asks'
import { expiredItemId } from '../questions/expired'
import { QuestionCard } from '../questions/QuestionCard'
import { byNeed, QuestionFlags, QuestionRevised, useExpiryClock } from '../questions/waiting'
import { useBoardState } from '../store/context'
import { cx } from '../transcript/bits'
import { minimized } from './docks'
import { QuestionIcon } from './icons'

/** The line that leads to the session's open expired questions in the transcript: Show goes to the first. */
function ExpiredLine({ questions }: { questions: readonly Question[] }) {
  const open = questions.filter((question) => question.state === 'open')
  if (open.length === 0) return null
  const show = () => {
    const item = document.getElementById(expiredItemId(open[0]!.id))
    item?.scrollIntoView?.({ block: 'center' })
    item?.focus()
  }
  return (
    <p className="asks-expired">
      <span>{open.length} expired in the transcript</span>{' '}
      <button type="button" className="button button-quiet asks-expired-show" onClick={show}>
        Show
      </button>
    </p>
  )
}

export function QuestionDock({ session, questions, expired = [] }: { session: string; questions: readonly Question[]; expired?: readonly Question[] }) {
  const { me, issues } = useBoardState()
  const member = me?.member?.id
  const [small, setSmall] = useState(() => minimized.has(session))
  const [selected, setSelected] = useState<string | null>(() => byNeed(questions, Date.now())[0]?.id ?? null)
  const [focus, setFocus] = useState<'card' | 'answer' | null>(null)
  const root = useRef<HTMLElement>(null)
  const cardId = useId()
  const byQuestion = useQuestionEntries()
  const now = useExpiryClock(questions)

  const shrink = (to: boolean) => {
    if (to) minimized.add(session)
    else minimized.delete(session)
    setSmall(to)
    setFocus(to ? 'answer' : 'card')
  }

  // Where the keyboard goes once the dock is drawn again: into the card he opened, or to the Answer that opens it.
  useEffect(() => {
    if (!focus) return
    const dock = root.current
    const target = focus === 'answer' ? dock?.querySelector<HTMLElement>('.asks-bar-open') : (dock?.querySelector<HTMLElement>('.asks-page:not([hidden]) .question-card-body :is(input, textarea)') ?? dock)
    target?.focus()
    setFocus(null)
  }, [focus])

  if (questions.length === 0) {
    return expired.some((question) => question.state === 'open') ? (
      <section className="code-questions" aria-label="Expired questions">
        <ExpiredLine questions={expired} />
      </section>
    ) : null
  }
  const ordered = byNeed(questions, now)
  const shown = Math.max(0, ordered.findIndex((question) => question.id === selected))
  const question = ordered[shown]!
  if (selected !== question.id) setSelected(question.id)
  const several = questions.length > 1
  const mine = putTo(question, member)
  const badge = mine ? 'Asks you' : 'Question'
  const asked = askedAt(question.id, byQuestion)
  const revised = question.revised_at

  // The whole line opens it under the pointer; Answer is its button for the keyboard.
  const bar = small && (
    <div className="asks-bar" onClick={() => shrink(false)}>
      <span className="asks-badge">
        <QuestionIcon size={14} />
        {badge}
        {several && (
          <span className="asks-count">
            {questions.length}
            <span className="visually-hidden"> questions</span>
          </span>
        )}
      </span>
      <span className="asks-bar-title">{askTitle(question)}</span>
      <QuestionFlags question={question} now={now} />
      {asked && <span className="asks-bar-age">{longAge(asked, now)}</span>}
      <button
        type="button"
        className="button button-primary asks-bar-open"
        aria-expanded={false}
        onClick={(event) => {
          event.stopPropagation()
          shrink(false)
        }}
      >
        Answer
        <ChevronDownIcon size={12} className="asks-chevron is-up" />
      </button>
    </div>
  )

  const next = several ? ordered[(shown + 1) % ordered.length]! : null
  // The spaces are for a screen reader: the boxes they stand between take none.
  const head = (
    <div className="asks-head">
      <span className="asks-badge">
        <QuestionIcon size={14} />
        {badge}
      </span>{' '}
      <QuestionFlags question={question} now={now} />
      {(asked || revised) && (
        <>
          <span className="asks-when">
            {asked && (
              <>
                Asked <Time iso={asked} />
              </>
            )}
            {asked && revised && ' · '}
            <QuestionRevised question={question} now={now} first={!asked} />
          </span>{' '}
        </>
      )}
      <span className="asks-tools">
        {several && (
          <span className="asks-pager" role="group" aria-label="Questions">
            <button type="button" className="button button-quiet code-icon-button" aria-label="Previous question" disabled={shown === 0} onClick={() => setSelected(ordered[shown - 1]!.id)}>
              <ChevronLeftIcon size={14} />
            </button>
            <span className="asks-pager-text" aria-live="polite">
              {shown + 1} of {questions.length}
            </span>
            <button type="button" className="button button-quiet code-icon-button" aria-label="Next question" disabled={shown === ordered.length - 1} onClick={() => setSelected(ordered[shown + 1]!.id)}>
              <ChevronRightIcon size={14} />
            </button>
          </span>
        )}
        <button type="button" className="button button-quiet code-icon-button" aria-label="Minimize" title="Minimize" aria-expanded={true} aria-controls={cardId} onClick={() => shrink(true)}>
          <ChevronDownIcon size={14} />
        </button>
      </span>
    </div>
  )
  return (
    <section className="code-questions" aria-label={badge} ref={root} tabIndex={small ? undefined : -1}>
      <ExpiredLine questions={expired} />
      {bar}
      <div className={cx('asks-dock', mine && 'is-mine')} id={cardId} hidden={small}>
        {head}
        {ordered.map((each) => (
          <div key={each.id} className="asks-page" hidden={each !== question}>
            <QuestionCard question={each} issue={each.issue ? issues.get(each.issue) : undefined} docked foot={next && each === question && <span className="asks-next">Next: {askTitle(next)}</span>} />
          </div>
        ))}
      </div>
    </section>
  )
}
